import React, { useState, useEffect, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';

// Detect if device is mobile/touch
const isTouchDevice = () => {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

// Merchant Search Select Component - Reusable searchable merchant dropdown
const MerchantSearchSelect = ({ merchants, value, onChange, placeholder = "All Merchants", className = "w-40 sm:w-48" }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = React.useRef(null);
  const isTouch = isTouchDevice();
  
  const filteredMerchants = useMemo(() => {
    if (!searchTerm) return merchants;
    return merchants.filter(merchant => 
      merchant.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [merchants, searchTerm]);
  
  const selectedMerchant = merchants.find(m => m.id === value);
  
  // Auto-focus search input when dropdown opens (only on desktop)
  useEffect(() => {
    if (isOpen && searchInputRef.current && !isTouch) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
    if (!isOpen) {
      setSearchTerm(''); // Clear search when closed
    }
  }, [isOpen, isTouch]);
  
  const handleOpenChange = (open) => {
    // On touch devices, add a small delay to prevent immediate close
    if (isTouch && open) {
      setTimeout(() => setIsOpen(true), 10);
    } else {
      setIsOpen(open);
    }
  };

  // Prevent dropdown from closing when interacting with search
  const preventClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  return (
    <Select 
      value={value} 
      onValueChange={(val) => {
        onChange(val);
        setSearchTerm('');
        setIsOpen(false);
      }}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder}>
          {value && value !== 'all'
            ? selectedMerchant?.name || "Merchant"
            : placeholder
          }
        </SelectValue>
      </SelectTrigger>
      <SelectContent 
        className="max-h-[400px]"
        position="popper"
        sideOffset={4}
        onPointerDownOutside={(e) => {
          // Check if click is on the search input area
          const target = e.target;
          if (target?.closest?.('[data-search-container]') || target?.tagName === 'INPUT') {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          const target = e.target;
          if (target?.closest?.('[data-search-container]') || target?.tagName === 'INPUT') {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={() => setIsOpen(false)}
      >
        {/* Search Input - Fixed for reliable text input */}
        <div 
          data-search-container="true"
          className="sticky top-0 z-50 bg-white border-b p-2"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search merchants..."
            value={searchTerm}
            onChange={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setSearchTerm(e.target.value);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Escape') {
                setIsOpen(false);
              }
            }}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            style={{ WebkitUserSelect: 'text', userSelect: 'text' }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-testid="merchant-search-input"
            onClick={(e) => {
              e.stopPropagation();
              e.target.focus();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            onFocus={(e) => {
              e.stopPropagation();
              e.target.select();
            }}
          />
        </div>
        
        {/* Results */}
        <div className="max-h-[300px] overflow-y-auto">
          <SelectItem value="all">{placeholder}</SelectItem>
          {filteredMerchants.length > 0 ? (
            filteredMerchants
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((merchant) => (
                <SelectItem key={merchant.id} value={merchant.id}>
                  {merchant.name}
                </SelectItem>
              ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500 text-center">
              No merchants found
            </div>
          )}
        </div>
      </SelectContent>
    </Select>
  );
};

export default MerchantSearchSelect;
